import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '../../api/endpoints.js';
import { useNotice } from '../../state/NoticeContext.jsx';
import {
  Banner,
  Button,
  Card,
  Grid2,
  Modal,
  Muted,
  Select,
  Spinner,
  Steps,
  Tag,
  Text,
  fmtDate,
  useAsync
} from '../../ui.jsx';

const statusTone = {
  submitted: 'mid',
  under_review: '',
  offered: 'ok',
  rejected: 'no',
  withdrawn: ''
};

const DOCUMENT_LABELS = {
  photo: 'Passport photograph',
  ghanaCard: 'Ghana Card',
  resultSlip: 'WASSCE result slip',
  referenceLetter: 'Reference letter'
};

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const notice = useNotice();
  const { data, loading, error, reload } = useAsync(() => adminApi.application(id), [id]);
  const [busy, setBusy] = useState('');

  const [programmeId, setProgrammeId] = useState('');
  const [mode, setMode] = useState('Regular');
  const [hall, setHall] = useState('');
  const [note, setNote] = useState('');
  const [confirmReject, setConfirmReject] = useState(false);

  if (loading) return <Spinner label="Loading application" />;
  if (error || !data) {
    return (
      <Card>
        <h2>Application not available</h2>
        <Muted>It was not found at your school, or the reference is not valid.</Muted>
        <Link to="/admin/applications" className="btn" style={{ textDecoration: 'none' }}>Back to applications</Link>
      </Card>
    );
  }

  const { application, applicant, profile, result, programmes, answers, commonDocuments, schoolDocuments, questions, payment } = data;

  const act = async (key, fn) => {
    setBusy(key);
    try {
      const data2 = await fn();
      notice.success(data2.message || 'Done.');
      setNote('');
      await reload();
    } catch (err) {
      notice.fromError(err);
    } finally {
      setBusy('');
    }
  };

  const startReview = () => act('review', () => adminApi.review(application.id, {}));
  const makeOffer = () =>
    act('offer', () =>
      adminApi.offer(application.id, { programmeId, mode, hall: hall.trim() })
    );
  const reject = () => {
    setConfirmReject(true);
  };

  const doReject = () =>
    act('reject', () => adminApi.reject(application.id, { note: note.trim() })).then(() =>
      setConfirmReject(false)
    );

  const answerFor = (key) => (answers || []).find((a) => a.key === key)?.value || '';

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Link to="/admin/applications" className="btn s sm" style={{ textDecoration: 'none' }}>
          &larr; Back to applications
        </Link>
      </div>

      <div className="row sp" style={{ marginBottom: 4 }}>
        <div>
          <h2 style={{ margin: 0 }}>{applicant.name}</h2>
          <Muted>
            {application.reference} · submitted {fmtDate(application.submittedAt)}
          </Muted>
        </div>
        <Tag tone={statusTone[application.status]}>{application.status.replace(/_/g, ' ')}</Tag>
      </div>

      <Card>
        <Steps
          current={application.progress}
          items={['Submitted', 'Received', 'Under review', 'Decision']}
        />
        {application.awaitingResults ? (
          <Banner title="Awaiting WASSCE results">
            This applicant said they are waiting for results, so consider the application once they
            are released.
          </Banner>
        ) : null}
        {application.status === 'withdrawn' ? (
          <Banner title="Withdrawn">
            The applicant withdrew this application on {fmtDate(application.withdrawnAt || application.decidedAt)}.
          </Banner>
        ) : null}
        {application.decisionNote ? (
          <div className="note">
            <b>Note to the applicant:</b> {application.decisionNote}
          </div>
        ) : null}
      </Card>

      {/*
        The decision is the only thing staff come here to do, and it used to sit
        below the applicant, results, programmes, answers, documents and
        questions, so "Start review" was off the bottom of a long scroll. It is
        now the first actionable thing on the page.
      */}
      {application.status !== 'withdrawn' && application.status !== 'rejected' ? (
        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Decision</h3>

          {application.status === 'submitted' ? (
            <>
              <Muted>Starting review locks the application, so the applicant can no longer change their answers.</Muted>
              <div style={{ marginTop: 10 }}>
                <Button busy={busy === 'review'} onClick={startReview}>Start review</Button>
              </div>
            </>
          ) : (
            <>
              <Muted>Make an offer on one of the programmes they chose, or reject the application.</Muted>
              <div style={{ marginTop: 12 }}>
                <Select
                  label="Offer this programme"
                  value={programmeId}
                  onChange={(e) => setProgrammeId(e.target.value)}
                >
                  <option value="">Choose a programme</option>
                  {programmes.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Select>
              </div>
              <div style={{ height: 12 }} />
              <div className="row">
                <div style={{ flex: 1, minWidth: 140 }}>
                  <Select label="Mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                    <option value="Regular">Regular</option>
                    <option value="Fee-paying">Fee-paying</option>
                    <option value="Sandwich">Sandwich</option>
                    <option value="Distance">Distance</option>
                  </Select>
                </div>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <Text
                    label="Hall"
                    hint="Optional."
                    value={hall}
                    onChange={(e) => setHall(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Button busy={busy === 'offer'} disabled={!programmeId} onClick={makeOffer}>
                  Make an offer
                </Button>
              </div>

              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--br)' }}>
                <Text
                  label="Rejection note"
                  hint="The applicant sees this. Leave empty to reject without one."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div style={{ marginTop: 10 }}>
                  <Button variant="s" onClick={reject}>
                    Reject application
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      ) : null}

      {confirmReject ? (
        <Modal
          title="Reject this application?"
          labelledBy="reject-title"
          onClose={() => setConfirmReject(false)}
          footer={
            <>
              <Button variant="s" busy={busy === 'reject'} onClick={doReject}>
                Yes, reject {applicant.name}
              </Button>
              <Button variant="s" onClick={() => setConfirmReject(false)}>Cancel</Button>
            </>
          }
        >
          {applicant.name} is told the application was rejected
          {note.trim() ? ' with the note you wrote' : ' with no note'}, and the decision cannot be
          undone from here. Offers already made are released.
        </Modal>
      ) : null}

      <Grid2>
        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Applicant</h3>
          <dl className="kv">
            <dt>Name</dt>
            <dd>{applicant.name}</dd>
            <dt>Email</dt>
            <dd>{applicant.email}{!applicant.emailVerified ? ' (not verified)' : ''}</dd>
            <dt>Phone</dt>
            <dd>{applicant.phone || 'Not given'}</dd>
            <dt>Ghana Card</dt>
            <dd>{profile?.ghanaCardNumber || 'Not given'}</dd>
            <dt>Date of birth</dt>
            <dd>{profile?.dateOfBirth ? fmtDate(profile.dateOfBirth) : 'Not given'}</dd>
            <dt>Gender</dt>
            <dd>{profile?.gender || 'Not given'}</dd>
            <dt>Region</dt>
            <dd>{profile?.region || 'Not given'}</dd>
            <dt>Home town</dt>
            <dd>{profile?.homeTown || 'Not given'}</dd>
            <dt>Guardian</dt>
            <dd>{profile?.guardianName || 'Not given'} {profile?.guardianPhone ? `· ${profile.guardianPhone}` : ''}</dd>
            <dt>Previous school</dt>
            <dd>{profile?.previousSchool || 'Not given'}</dd>
            <dt>Index number</dt>
            <dd>{profile?.indexNumber || 'Not given'}</dd>
            <dt>Special needs</dt>
            <dd>{profile?.specialNeeds || 'None declared'}</dd>
          </dl>
        </Card>

        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Results</h3>
          {result ? (
            <>
              <div className="row sp">
                <b>Aggregate {result.aggregate}</b>
                {result.provisional ? <Tag tone="mid">Provisional</Tag> : null}
              </div>
              <dl className="kv" style={{ marginTop: 10 }}>
                <dt>English</dt>
                <dd>{result.eng || 'Not given'}</dd>
                <dt>Mathematics</dt>
                <dd>{result.math || 'Not given'}</dd>
                <dt>Science</dt>
                <dd>{result.sci || 'Not given'}</dd>
                <dt>Social Studies</dt>
                <dd>{result.soc || 'Not given'}</dd>
                {result.electives.map((e) => (
                  <span key={e.code} style={{ display: 'contents' }}>
                    <dt>{e.subject}</dt>
                    <dd>{e.grade}</dd>
                  </span>
                ))}
              </dl>
            </>
          ) : (
            <Muted>This applicant has not added their results yet.</Muted>
          )}

          <h3 style={{ fontSize: 18, margin: '18px 0 8px' }}>Payment</h3>
          {payment ? (
            <dl className="kv">
              <dt>Status</dt>
              <dd>{payment.status}</dd>
              <dt>Reference</dt>
              <dd>{payment.reference || 'Not set'}</dd>
              <dt>Application fee</dt>
              <dd>GH{payment.applicationFee}</dd>
              <dt>Service fee</dt>
              <dd>GH{payment.serviceFee}</dd>
              <dt>Total</dt>
              <dd>GH{payment.total}</dd>
              <dt>Paid</dt>
              <dd>{payment.paidAt ? fmtDate(payment.paidAt) : 'Not yet'}</dd>
            </dl>
          ) : (
            <Muted>No payment has been started for this application.</Muted>
          )}
        </Card>
      </Grid2>

      <Card>
        <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Programme choices</h3>
        {programmes.length === 0 ? (
          <Muted>No programme was chosen.</Muted>
        ) : (
          programmes.map((programme) => (
            <div className="prog" key={programme.id}>
              <div style={{ flex: 1 }}>
                <b>{programme.name}</b>
                <div className="mu">{programme.ruleText}</div>
                {programme.note ? <div className="mu">{programme.note}</div> : null}
              </div>
              <div style={{ textAlign: 'right' }}>
                {programme.cutOff == null ? (
                  <Tag tone="mid">No cut-off</Tag>
                ) : (
                  <Tag tone={programme.meetsCutOff ? 'ok' : 'no'}>
                    Cut-off {programme.cutOff}
                    {programme.meetsCutOff ? ' · meets' : ' · below'}
                  </Tag>
                )}
              </div>
            </div>
          ))
        )}
      </Card>

      {questions?.length ? (
        <Card>
          <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Answers to your questions</h3>
          {questions.map((q) => (
            <div className="prog" key={q.key}>
              <div style={{ flex: 1 }}>
                <b>{q.label}</b>
                <div className="mu">{answerFor(q.key) || 'No answer'}</div>
              </div>
            </div>
          ))}
        </Card>
      ) : null}

      <Card>
        <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>Documents</h3>
        {Object.entries(commonDocuments).map(([key, doc]) => (
          <div className="prog" key={key}>
            <div style={{ flex: 1 }}>
              <b>{DOCUMENT_LABELS[key] || key}</b>
              <div className="mu">{doc.originalName}</div>
            </div>
            <a className="btn sm" href={doc.url} target="_blank" rel="noreferrer noopener" style={{ textDecoration: 'none' }}>
              Open
            </a>
          </div>
        ))}
        {schoolDocuments.map((doc) => (
          <div className="prog" key={doc.id}>
            <div style={{ flex: 1 }}>
              <b>School document</b>
              <div className="mu">{doc.originalName}</div>
            </div>
            <a className="btn sm" href={doc.url} target="_blank" rel="noreferrer noopener" style={{ textDecoration: 'none' }}>
              Open
            </a>
          </div>
        ))}
        {Object.keys(commonDocuments).length + schoolDocuments.length === 0 ? (
          <Muted>No documents were attached to this application.</Muted>
        ) : null}
        <Muted>Links are signed and expire, so re-open this page to get a fresh one.</Muted>
      </Card>

      <Card>
        <h3 style={{ fontSize: 18, margin: '0 0 8px' }}>History</h3>
        {(application.history || []).map((entry, i) => (
          <div className="prog" key={`${entry.at}-${i}`}>
            <div style={{ flex: 1 }}>
              <b>{String(entry.to || '').replace(/_/g, ' ')}</b>
              {entry.note ? <div className="mu">{entry.note}</div> : null}
            </div>
            <span className="mu">{fmtDate(entry.at)}</span>
          </div>
        ))}
      </Card>
    </>
  );
}
